import * as React from 'react';

interface APIFetchProps {
	cacheTtl?: number;
}

export interface IItemsCounts {
	actionableTotalItems?: number;
	totalItems: number;
}

export interface APIMeta extends IItemsCounts {
	currentPage: number;
	itemsPerPage: number;
	totalPages: number;
}

interface FetchConfig {
	args?: any[];
	data?: Record<string, unknown>;
	disableCache?: boolean;
	forceRefresh?: boolean; // Needed for busting the cache
	method: (...args: any[]) => any;
	params?: Record<string, unknown>;
	responseParser?: (response: any) => any[];
	overrideCacheKey?: string;
}

interface CacheEntry {
	data: any;
	expiresAt: number;
}

const DEFAULT_CACHE_TTL = 60000;

function useApiFetch({ cacheTtl = DEFAULT_CACHE_TTL }: APIFetchProps = {}) {
	const abortControllerRef = React.useRef<AbortController | null>(null);
	const cacheRef = React.useRef<Map<string, CacheEntry>>(new Map());
	const cleanupIntervalRef = React.useRef<number | null>(null);

	const cleanExpiredCache = React.useCallback(() => {
		const now = Date.now();

		for (const [key, entry] of cacheRef.current.entries()) {
			if (now >= entry.expiresAt) {
				cacheRef.current.delete(key);
			}
		}
	}, []);

	const clearCache = React.useCallback(() => {
		cacheRef.current.clear();
	}, []);

	// Periodic cache cleanup
	React.useEffect(() => {
		// Clean expired entries every minute
		cleanupIntervalRef.current = setInterval(cleanExpiredCache, 60000) as unknown as number;

		return () => {
			if (cleanupIntervalRef.current) {
				clearInterval(cleanupIntervalRef.current);
			}
		};
	}, [cleanExpiredCache]);

	const fetchData = React.useCallback(
		async ({
			args = [],
			data = {},
			disableCache = false,
			forceRefresh = false,
			method,
			params = {},
			responseParser,
			overrideCacheKey,
		}: FetchConfig) => {
			const cacheKey =
				overrideCacheKey ||
				JSON.stringify({
					method: method.name,
					args,
					params,
					data,
				});

			// Check cache and expiration
			if (!disableCache && cacheRef.current.has(cacheKey)) {
				const cacheEntry = cacheRef.current.get(cacheKey)!;

				if (!forceRefresh && Date.now() < cacheEntry.expiresAt) {
					return cacheEntry.data;
				}

				// Remove expired entry or delete if forced
				cacheRef.current.delete(cacheKey);
			}

			// Cancel any pending requests
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create new AbortController for this request
			abortControllerRef.current = new AbortController();
			const controller = abortControllerRef.current;

			try {
				const apiData = {
					...data,
					...params,
				};

				let callArgs = [...args];

				// No API args provided
				if (callArgs.length === 0) {
					callArgs = [apiData];
				}
				// API args contains a partial object of data
				else if (typeof callArgs[1] === 'object') {
					callArgs[1] = { ...callArgs[1], ...apiData };
				}
				// Set `apiData` as the default second argument
				else {
					callArgs = [...callArgs, apiData];
				}

				/**
				 * NOTE: This needs to be moved to Tanstack Query once we have the ability
				 * to use npm packages in the `ui` package.
				 */
				const response = await method(...callArgs, { signal: controller.signal });

				// A superseded request must not surface its result or error state; some network layers
				// resolve cancelled requests with an { err } sentinel instead of rejecting.
				if (controller.signal.aborted) {
					return;
				}

				if (!response || response?.err || (response?.status && response?.status >= 400)) {
					// return;
					throw new Error('API request failed while using useApiFetch');
				}

				let parsedResponse = response;
				if (responseParser) {
					parsedResponse = responseParser(response);
				}

				// Cache the result with expiration unless caching is disabled
				if (!disableCache) {
					cacheRef.current.set(cacheKey, {
						data: parsedResponse,
						expiresAt: Date.now() + cacheTtl,
					});
				}

				return parsedResponse;
			} catch (e: unknown) {
				if (controller.signal.aborted) {
					return;
				}

				// Handle other errors
				throw e;
			}
		},
		[cacheTtl]
	);

	// Cleanup abort controller and interval on unmount
	React.useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			if (cleanupIntervalRef.current) {
				clearInterval(cleanupIntervalRef.current);
			}
		};
	}, []);

	return { fetchData, clearCache };
}

export default useApiFetch;
